'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Check, AlertCircle, Loader2, X, Mail, KeyRound, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import {
  RESEND_COOLDOWN_SECONDS,
  normalizeEmail,
  resetEmailError as getResetEmailError,
  isSixDigitCode,
  newPasswordError,
  requestErrorMessage,
  verifyErrorMessage,
  updateErrorMessage,
  setRecoveryInProgress,
  isRecoveryInProgress,
  NETWORK_MESSAGE,
} from '@/lib/auth/passwordRecovery';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const roleRedirectMap: Record<string, string> = {
  super_admin: '/super-admin-dashboard',
  admin: '/vehicle-management',
  manager: '/vehicle-management',
  sales: '/vehicle-management',
  operator: '/vehicle-management',
  viewer: '/vehicle-management',
};

// ── Forgot Password Steps (Supabase recovery with a 6-digit code) ─────────────
type ForgotStep = 'email' | 'otp' | 'password' | 'success';

export default function LoginForm() {
  const { signIn, signOut } = useAuth();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isSubscriptionError, setIsSubscriptionError] = useState(false);

  // Rate limit / backoff state
  const [rateLimited, setRateLimited] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const failedAttemptsRef = useRef(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Forgot Password State ──────────────────────────────────────────────────
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState<ForgotStep>('email');

  // Step 1 – email
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailLoading, setResetEmailLoading] = useState(false);
  const [resetEmailError, setResetEmailError] = useState('');

  // Step 2 – OTP
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step 3 – new password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatePasswordLoading, setUpdatePasswordLoading] = useState(false);
  const [updatePasswordError, setUpdatePasswordError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: true },
  });

  // Leaving the login screen while the dialog holds a recovery session: end that session. Does nothing when
  // no recovery is in progress (normal logins, React strict-mode remount); the flag is cleared even if
  // signing out fails.
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  useEffect(() => {
    return () => {
      if (!isRecoveryInProgress()) return;
      void (async () => {
        try {
          await signOutRef.current();
        } catch {
          // the session may already be gone
        } finally {
          setRecoveryInProgress(false);
        }
      })();
    };
  }, []);

  // Resend countdown effect
  useEffect(() => {
    if (resendCountdown <= 0) {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      return;
    }
    resendTimerRef.current = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) {
          if (resendTimerRef.current) clearInterval(resendTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, [resendCountdown]);

  const startBackoff = (seconds: number) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setRateLimited(true);
    setCountdown(seconds);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          setRateLimited(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 1 (and "Resend Code"): Supabase sends the recovery email. The answer never says whether the
  // email exists: only rate limits and network failures are shown.
  const requestRecoveryCode = async (): Promise<string | null> => {
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(normalizeEmail(resetEmail), {
        redirectTo: `${window.location.origin}/auth/callback`,
      });
      return requestErrorMessage(error);
    } catch {
      return NETWORK_MESSAGE;
    }
  };

  const handleResendOtp = async () => {
    if (resendCountdown > 0) return;
    setOtpError('');
    setOtpCode('');
    setResendCountdown(RESEND_COOLDOWN_SECONDS);
    const error = await requestRecoveryCode();
    if (error) {
      setOtpError(error);
    } else {
      toast.success('If an account exists, a new code has been sent.');
    }
  };

  // Ends a recovery session held by this dialog (other devices were already signed out after a reset).
  const endRecoverySession = async () => {
    if (!isRecoveryInProgress()) return;
    try {
      await signOut();
    } catch {
      // the session may already be gone
    } finally {
      setRecoveryInProgress(false);
    }
  };

  // Step 3: inside the session verifyOtp created; then every other session of this user is signed out,
  // and this one too, so the user signs in again with the new password.
  const handleUpdatePassword = async () => {
    const ruleError = newPasswordError(newPassword, confirmPassword);
    if (ruleError) {
      setUpdatePasswordError(ruleError);
      return;
    }
    setUpdatePasswordLoading(true);
    setUpdatePasswordError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setUpdatePasswordError(updateErrorMessage(error));
        return;
      }
      await supabase.auth.signOut({ scope: 'others' }).catch(() => {});
      await endRecoverySession();
      setForgotStep('success');
    } catch {
      setUpdatePasswordError(NETWORK_MESSAGE);
    } finally {
      setUpdatePasswordLoading(false);
    }
  };

  const closeForgotModal = () => {
    void endRecoverySession();
    setShowForgotModal(false);
    setForgotStep('email');
    setResetEmail('');
    setResetEmailError('');
    setOtpCode('');
    setOtpError('');
    setNewPassword('');
    setConfirmPassword('');
    setUpdatePasswordError('');
    setResendCountdown(0);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
  };

  const handleSendOtp = async () => {
    const emailError = getResetEmailError(resetEmail);
    if (emailError) {
      setResetEmailError(emailError);
      return;
    }
    setResetEmailLoading(true);
    setResetEmailError('');
    try {
      const error = await requestRecoveryCode();
      if (error) {
        setResetEmailError(error);
        return;
      }
      setForgotStep('otp');
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
    } finally {
      setResetEmailLoading(false);
    }
  };

  // Step 2: a correct code signs the user in with a recovery session (kept on this screen by the flag).
  const handleVerifyOtp = async () => {
    if (!isSixDigitCode(otpCode)) {
      setOtpError('Please enter the 6-digit verification code.');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    setRecoveryInProgress(true);
    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.verifyOtp({
        email: normalizeEmail(resetEmail),
        token: otpCode,
        type: 'recovery',
      });
      if (error || !data?.session) {
        setRecoveryInProgress(false);
        setOtpError(verifyErrorMessage(error));
        return;
      }
      setForgotStep('password');
    } catch {
      setRecoveryInProgress(false);
      setOtpError(NETWORK_MESSAGE);
    } finally {
      setOtpLoading(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    if (rateLimited) return;
    setIsLoading(true);
    setLoginError('');
    setIsSubscriptionError(false);
    try {
      const raw = searchParams.get('redirectTo');
      const redirectTo = raw?.startsWith('/') && !raw.startsWith('//') ? raw : null;
      const result = await signIn(data.email, data.password, data.remember);
      failedAttemptsRef.current = 0;
      const role = result?.role ?? '';
      const destination = redirectTo || roleRedirectMap[role] || '/vehicle-management';
      window.location.href = destination;
    } catch (err: unknown) {
      failedAttemptsRef.current += 1;
      const attempts = failedAttemptsRef.current;
      const message = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      if (message.toLowerCase().includes('subscription') || message.toLowerCase().includes('suspended')) {
        setIsSubscriptionError(true);
      }
      setLoginError(message);
      reset({ email: getValues('email'), password: '', remember: getValues('remember') });
      clearErrors();
      if (attempts >= 5) {
        startBackoff(300);
      } else if (attempts >= 3) {
        startBackoff(30);
      }
      setIsLoading(false);
    }
  };

  // ── Standard Login UI ──
  return (
    <div className="animate-fade-in">
      <div className="mb-7">
        <h2 className="text-2xl font-800 text-slate-900">Welcome back</h2>
        <p className="text-sm text-slate-500 mt-1">Sign in to your MotoDesk account</p>
      </div>

      {/* Session expired banner */}
      {searchParams?.get('reason') === 'session_expired' && (
        <div className="flex items-start gap-2 rounded-lg px-4 py-3 mb-4 bg-amber-50 border border-amber-200 animate-slide-up">
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5 text-amber-600" />
          <p className="text-sm text-amber-700">
            Your session has expired due to inactivity. Please log in again.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Email */}
        <div>
          <label htmlFor="email" className="form-label">Email address</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@dealership.com"
            suppressHydrationWarning
            className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-400/30 focus:border-red-400' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="form-error">
              <AlertCircle size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="form-label mb-0">Password</label>
            <button
              type="button"
              suppressHydrationWarning
              onClick={() => { setShowForgotModal(true); setForgotStep('email'); }}
              className="text-xs text-blue-700 font-600 hover:underline"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              suppressHydrationWarning
              className={`input-field pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400/30 focus:border-red-400' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              suppressHydrationWarning
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="form-error">
              <AlertCircle size={12} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Global error */}
        {rateLimited ? (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 animate-slide-up">
            <AlertCircle size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-amber-800">Too many sign-in attempts</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Please wait{' '}
                <span className="font-700 tabular-nums">
                  {countdown >= 60
                    ? `${Math.floor(countdown / 60)}m ${countdown % 60}s`
                    : `${countdown}s`}
                </span>{' '}
                before trying again.
              </p>
            </div>
          </div>
        ) : loginError ? (
          <div className={`flex items-start gap-2 rounded-lg px-4 py-3 animate-slide-up ${isSubscriptionError ? 'bg-amber-50 border border-amber-300' : 'bg-red-50 border border-red-200'}`}>
            <AlertCircle size={15} className={`flex-shrink-0 mt-0.5 ${isSubscriptionError ? 'text-amber-600' : 'text-red-600'}`} />
            <div>
              {isSubscriptionError && (
                <p className="text-sm font-semibold text-amber-800 mb-1">Account Suspended</p>
              )}
              <p className={`text-sm ${isSubscriptionError ? 'text-amber-700' : 'text-red-700'}`}>{loginError}</p>
            </div>
          </div>
        ) : null}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || rateLimited}
          suppressHydrationWarning
          className="w-full bg-blue-800 text-white py-3 rounded-xl text-sm font-700 hover:bg-blue-900 active:scale-[0.99] transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ minHeight: 48 }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in...
            </>
          ) : rateLimited ? (
            <>
              <Loader2 size={16} className="animate-spin text-white/70" />
              Retry in {countdown >= 60 ? `${Math.floor(countdown / 60)}m ${countdown % 60}s` : `${countdown}s`}
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      {/* ── Forgot Password Modal ── */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 relative animate-fade-in">
            <button
              type="button"
              onClick={closeForgotModal}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Step 1: Email */}
            {forgotStep === 'email' && (
              <>
                <div className="mb-5">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <Mail size={18} className="text-blue-700" />
                  </div>
                  <h3 className="text-lg font-700 text-slate-900 mb-1">Forgot Password</h3>
                  <p className="text-sm text-slate-500">Enter your email address and we&apos;ll send you a verification code.</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="reset-email" className="form-label">Email Address</label>
                    <input
                      id="reset-email"
                      type="email"
                      placeholder="you@dealership.com"
                      value={resetEmail}
                      onChange={e => { setResetEmail(e.target.value); setResetEmailError(''); }}
                      className={`input-field ${resetEmailError ? 'border-red-400' : ''}`}
                      onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                      suppressHydrationWarning
                    />
                    {resetEmailError && (
                      <p className="form-error">
                        <AlertCircle size={12} />
                        {resetEmailError}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={resetEmailLoading}
                    className="w-full bg-blue-800 text-white py-2.5 rounded-xl text-sm font-700 hover:bg-blue-900 active:scale-[0.99] transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {resetEmailLoading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Sending code...
                      </>
                    ) : (
                      'Send Verification Code'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeForgotModal}
                    className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors py-1"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {/* Step 2: OTP Verification */}
            {forgotStep === 'otp' && (
              <>
                <div className="mb-5">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <KeyRound size={18} className="text-blue-700" />
                  </div>
                  <h3 className="text-lg font-700 text-slate-900 mb-1">Enter Verification Code</h3>
                  <p className="text-sm text-slate-500">
                    If an account exists for{' '}
                    <span className="font-600 text-slate-700">{resetEmail}</span>, we sent a 6-digit code to it.
                    Check your spam folder too. The code works only for a limited time.
                  </p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="otp-code" className="form-label">Verification Code</label>
                    <input
                      id="otp-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={otpCode}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(val);
                        setOtpError('');
                      }}
                      className={`input-field text-center text-2xl font-800 tracking-[0.5em] ${otpError ? 'border-red-400' : ''}`}
                      onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                      suppressHydrationWarning
                    />
                    {otpError && (
                      <p className="form-error">
                        <AlertCircle size={12} />
                        {otpError}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={otpLoading}
                    className="w-full bg-blue-800 text-white py-2.5 rounded-xl text-sm font-700 hover:bg-blue-900 active:scale-[0.99] transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {otpLoading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendCountdown > 0}
                      className="text-sm text-blue-700 font-600 hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      {resendCountdown > 0 ? `Resend Code (${resendCountdown}s)` : 'Resend Code'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setForgotStep('email'); setOtpCode(''); setOtpError(''); }}
                      className="text-sm text-slate-500 hover:text-slate-700"
                    >
                      Change email
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 3: New Password */}
            {forgotStep === 'password' && (
              <>
                <div className="mb-5">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                    <Lock size={18} className="text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-700 text-slate-900 mb-1">Set New Password</h3>
                  <p className="text-sm text-slate-500">Choose a strong password for your account.</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="new-password" className="form-label">New Password</label>
                    <div className="relative">
                      <input
                        id="new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Min. 8 characters"
                        value={newPassword}
                        onChange={e => { setNewPassword(e.target.value); setUpdatePasswordError(''); }}
                        className={`input-field pr-10 ${updatePasswordError ? 'border-red-400' : ''}`}
                        suppressHydrationWarning
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        suppressHydrationWarning
                      >
                        {showNewPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="confirm-password" className="form-label">Confirm Password</label>
                    <div className="relative">
                      <input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={e => { setConfirmPassword(e.target.value); setUpdatePasswordError(''); }}
                        className={`input-field pr-10 ${updatePasswordError ? 'border-red-400' : ''}`}
                        onKeyDown={e => e.key === 'Enter' && handleUpdatePassword()}
                        suppressHydrationWarning
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        suppressHydrationWarning
                      >
                        {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                  {updatePasswordError && (
                    <p className="form-error">
                      <AlertCircle size={12} />
                      {updatePasswordError}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleUpdatePassword}
                    disabled={updatePasswordLoading}
                    className="w-full bg-blue-800 text-white py-2.5 rounded-xl text-sm font-700 hover:bg-blue-900 active:scale-[0.99] transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {updatePasswordLoading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Updating password...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </>
            )}

            {/* Step 4: Success */}
            {forgotStep === 'success' && (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Check size={22} className="text-emerald-600" />
                </div>
                <h3 className="text-lg font-700 text-slate-900 mb-2">Password Updated</h3>
                <p className="text-sm text-slate-500 mb-5">
                  Password updated successfully. Please sign in using your new password.
                </p>
                <button
                  type="button"
                  onClick={closeForgotModal}
                  className="w-full bg-blue-800 text-white py-2.5 rounded-xl text-sm font-600 hover:bg-blue-900 transition-colors"
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}